import type { RJSFSchema } from '@rjsf/utils';
import type { WhatsappTemplate } from '@/features/campaigns/v2/types/whatsappTemplates';
import type { FlowFormData, ResolveStepAutoFill, StepAutoFillResult } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';

/** Template identity used to auto-complete Edit Template step1. `id` is optional when only catalog name/lang is known. */
export type WhatsappEditSelection = {
  name: string;
  language: string;
  id?: string;
};

export const formatWhatsappTemplateEnumValue = ({ name, language, id }: WhatsappEditSelection): string | undefined => {
  if (!id) return undefined;
  return `${name} [${language}] (${id})`;
};

/**
 * Match a Gupshup/Edit Template enum option.
 * 1. exact `name [language] (id)` when id is present
 * 2. includes(name) && includes(id)
 * 3. includes(name) && includes(`[language]`)
 * 4. unique includes(name)
 */
export const findWhatsappTemplateEnumValue = (
  enumValues: string[],
  selection: WhatsappEditSelection
): string | undefined => {
  const exact = formatWhatsappTemplateEnumValue(selection);
  if (exact && enumValues.includes(exact)) return exact;

  if (selection.id) {
    const byNameAndId = enumValues.find((value) => value.includes(selection.name) && value.includes(selection.id!));
    if (byNameAndId) return byNameAndId;
  }

  const languageToken = `[${selection.language}]`;
  const byNameAndLanguage = enumValues.filter((value) => value.includes(selection.name) && value.includes(languageToken));
  if (byNameAndLanguage.length >= 1) return byNameAndLanguage[0];

  const byNameOnly = enumValues.filter((value) => value.includes(selection.name));
  if (byNameOnly.length === 1) return byNameOnly[0];

  return undefined;
};

const collectStringEnums = (schema: RJSFSchema | undefined): Array<{ fieldKey: string; enumValues: string[] }> => {
  const properties = (schema?.properties ?? {}) as Record<string, { type?: unknown; enum?: unknown[] }>;
  const result: Array<{ fieldKey: string; enumValues: string[] }> = [];
  for (const [fieldKey, prop] of Object.entries(properties)) {
    if (!prop || !Array.isArray(prop.enum)) continue;
    const enumValues = prop.enum.filter((value): value is string => typeof value === 'string');
    if (enumValues.length > 0) result.push({ fieldKey, enumValues });
  }
  return result;
};

/** Look up a workspace WA template by catalog name/language to enrich selection with `id`. */
export const resolveWhatsappEditSelection = (
  templateName: string,
  templateLanguage: string | undefined,
  waTemplates: ReadonlyArray<Pick<WhatsappTemplate, 'id' | 'name' | 'language' | 'status'>>
): WhatsappEditSelection | undefined => {
  const byName = waTemplates.filter((template) => template.name === templateName);
  if (byName.length === 0) return undefined;

  const byLanguage = templateLanguage ? byName.filter((template) => template.language === templateLanguage) : byName;
  const pool = byLanguage.length > 0 ? byLanguage : byName;
  const preferred = pool.find((template) => template.status === 'approved') ?? pool[0];
  return { id: preferred.id, name: preferred.name, language: preferred.language };
};

/** Catalog Customize selection: always name+language; attach workspace `id` when available. */
export const buildWhatsappCatalogEditSelection = (
  templateName: string,
  templateLanguage: string,
  waTemplates: ReadonlyArray<Pick<WhatsappTemplate, 'id' | 'name' | 'language' | 'status'>>
): WhatsappEditSelection => {
  const fromWorkspace = resolveWhatsappEditSelection(templateName, templateLanguage, waTemplates);
  if (fromWorkspace) return fromWorkspace;
  return { name: templateName, language: templateLanguage };
};

export const createWhatsappEditFlowHandler = (selection: WhatsappEditSelection | undefined): ResolveStepAutoFill => {
  return ({ formSchema }): StepAutoFillResult | null => {
    if (!selection?.name) return null;

    const enums = collectStringEnums(formSchema.schema);
    if (enums.length === 0) return null;

    const preferred = enums.find((entry) => entry.fieldKey === 'template') ?? enums[0];
    const matchedValue = findWhatsappTemplateEnumValue(preferred.enumValues, selection);
    if (!matchedValue) return null;

    const data: FlowFormData = { [preferred.fieldKey]: matchedValue };
    return { data, skip: true };
  };
};

export const whatsappEditSelectionKey = (selection: WhatsappEditSelection | undefined): string => {
  if (!selection?.name) return 'none';
  return `${selection.name}|${selection.language}|${selection.id ?? 'pending'}`;
};
